import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles, CheckCircle2, ArrowDown, Compass, ShieldCheck,
    Users, BookOpen, Award, Star, Layers, Search, Handshake,
    ClipboardList, HandHeart, Focus, GraduationCap, Trophy,
    HeartHandshake, ScanEye, ThumbsUp, MessageCircle, Wrench,
} from "lucide-react";
import { COLLECTIONS } from "@/lib/collections";

// ---------------------------------------------------------------
// SEO
// ---------------------------------------------------------------
function useDocumentSEO(title, description) {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = title;
        let meta = document.querySelector('meta[name="description"]');
        const prev = meta ? meta.getAttribute("content") : null;
        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "description");
            document.head.appendChild(meta);
        }
        meta.setAttribute("content", description);
        return () => {
            document.title = prevTitle;
            if (meta && prev !== null) meta.setAttribute("content", prev);
        };
    }, [title, description]);
}

// ---------------------------------------------------------------
// HighLevel dedicated RECRUITMENT form (NOT the customer form).
// Customer form ID:      So4yQ0SZIxf6G4bxVWNM  → /soumission
// Recruitment form ID:   2XnIwz3Nb7pYOtvDB6oM  → this page
// The two must remain fully separated. Never route candidates through
// the customer form ID and vice-versa.
// ---------------------------------------------------------------
const HL_REC_SRC = "https://api.leadconnectorhq.com/widget/form/2XnIwz3Nb7pYOtvDB6oM";
const HL_REC_FORM_ID = "2XnIwz3Nb7pYOtvDB6oM";
const HL_SCRIPT_SRC = "https://link.msgsndr.com/js/form_embed.js";

function useHighLevelEmbedScript() {
    useEffect(() => {
        if (document.querySelector(`script[src="${HL_SCRIPT_SRC}"]`)) return;
        const s = document.createElement("script");
        s.src = HL_SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        document.body.appendChild(s);
    }, []);
}

// ---------------------------------------------------------------
// Realistic gem — <img> tag using the supplied premium assets.
// Each gem key maps to a file under /images/gems/. A soft halo in the
// gem's dominant colour is applied behind for luminosity and consistency
// with the Ménage KGD Collections universe.
// ---------------------------------------------------------------
const GEM_ASSETS = {
    diamant:  { src: "/images/gems/diamant.webp",  glow: "rgba(180,215,255,0.55)", alt: "Diamant photoréaliste — Collection Diamant" },
    emeraude: { src: "/images/gems/emeraude.webp", glow: "rgba(74,222,128,0.45)",  alt: "Émeraude photoréaliste — Collection Émeraude" },
    rubis:    { src: "/images/gems/rubis.webp",    glow: "rgba(239,68,68,0.45)",   alt: "Rubis photoréaliste — Collection Rubis" },
    perle:    { src: "/images/gems/perle.webp",    glow: "rgba(232,212,139,0.45)", alt: "Perle photoréaliste — Collection Perle" },
    saphir:   { src: "/images/gems/saphir.webp",   glow: "rgba(59,130,246,0.55)",  alt: "Saphir photoréaliste — Collection Saphir" },
};

function RealGem({ gem = "diamant", size = 64, glow = 0.9, className = "", decorative = false }) {
    const a = GEM_ASSETS[gem] || GEM_ASSETS.diamant;
    const dim = typeof size === "number" ? `${size}px` : size;
    return (
        <div className={`relative inline-block ${className}`} style={{ width: dim, height: dim }} aria-hidden={decorative ? "true" : undefined}>
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{ background: `radial-gradient(closest-side, ${a.glow}, transparent 70%)`, filter: "blur(6px)", opacity: glow, transform: "scale(1.4)" }}/>
            <img src={a.src} alt={decorative ? "" : a.alt} className="relative w-full h-full object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.35)]" loading="lazy" draggable="false"/>
        </div>
    );
}

// ---------------------------------------------------------------
// Level list — each level uses the DIAMANT image at increasing size &
// halo intensity to communicate raw potential → refinement → excellence.
// Using a single gem for the progression avoids confusion with the
// five distinct Collections presented in the callout below.
// ---------------------------------------------------------------
const LEVELS = [
    { n: 1, title: "Apprenti Collectionneur",  accent: "#94A3B8", size: 64, glow: 0.35,
      desc: "L'entrée dans l'univers KGD. L'Apprenti découvre nos méthodes, nos produits, notre organisation, nos standards de qualité et notre façon de prendre soin des espaces confiés par nos clients." },
    { n: 2, title: "Collectionneur KGD",       accent: "#075BC8", size: 76, glow: 0.55,
      desc: "Le Collectionneur maîtrise les fondations de la méthode KGD et peut réaliser ses interventions avec autonomie, tout en respectant les procédures, les attentes des clients et les standards de l'entreprise." },
    { n: 3, title: "Collectionneur Certifié",  accent: "#138A3D", size: 88, glow: 0.75,
      desc: "Cette certification reconnaît un niveau supérieur de constance, de maîtrise et de fiabilité. Le Collectionneur Certifié a démontré sa capacité à maintenir les standards KGD de façon durable." },
    { n: 4, title: "Maître de sa Collection",  accent: "#0369A1", size: 100, glow: 0.9,
      desc: "Le Collectionneur atteint une maîtrise approfondie de la Collection dans laquelle il évolue. Il connaît ses méthodes, ses exigences particulières et les standards de qualité associés." },
    { n: 5, title: "Maître Collectionneur KGD", accent: "#8C6D1F", size: 116, glow: 1.0,
      desc: "Le Maître Collectionneur représente un niveau avancé de maîtrise KGD. Au-delà de l'exécution, il est capable de transmettre les méthodes, d'accompagner d'autres Collectionneurs et de contribuer au maintien des standards de l'entreprise." },
];

const PATHS = [
    { icon: Layers, title: "Découvrir d'autres Collections",
      desc: "Un Collectionneur peut choisir de se former à une nouvelle Collection, développer de nouvelles compétences et progressivement acquérir plusieurs maîtrises au sein de l'univers KGD.",
      accent: "#138A3D" },
    { icon: Search, title: "Éclaireur KGD", note: "Qualification de confiance",
      desc: "L'Éclaireur représente Ménage KGD lors des visites d'évaluation. Il découvre les lieux, écoute les besoins du client, analyse les particularités de l'espace, documente les informations pertinentes et contribue à la préparation d'une proposition adaptée.",
      bullets: ["Effectuer les visites d'évaluation", "Analyser les lieux", "Découvrir les besoins du client", "Identifier les priorités", "Documenter l'état et les particularités", "Communiquer professionnellement", "Transmettre les informations pour la soumission"],
      future: "Spécialisations possibles à venir : Éclaireur Résidentiel, Éclaireur Saphir.",
      accent: "#075BC8" },
    { icon: ShieldCheck, title: "Gardien de Collection",
      desc: "Le Gardien de Collection contribue à protéger et maintenir les standards d'une Collection. Il peut participer au contrôle qualité, accompagner d'autres Collectionneurs, identifier les écarts et contribuer à l'amélioration continue.",
      accent: "#138A3D" },
    { icon: Handshake, title: "Ambassadeur KGD",
      desc: "L'Ambassadeur KGD incarne les valeurs de l'entreprise et peut contribuer à la représentation de la marque, à l'intégration de nouveaux membres, au leadership interne et au développement de la culture KGD.",
      accent: "#B8912B" },
];

const QUALITIES = [
    { icon: Focus, t: "Rigueur" },
    { icon: CheckCircle2, t: "Constance" },
    { icon: ShieldCheck, t: "Fiabilité" },
    { icon: HeartHandshake, t: "Respect" },
    { icon: Award, t: "Professionnalisme" },
    { icon: ScanEye, t: "Sens du détail" },
    { icon: GraduationCap, t: "Capacité d'apprentissage" },
    { icon: ThumbsUp, t: "Fierté du travail bien fait" },
    { icon: MessageCircle, t: "Bonne communication" },
];

const OFFERS = [
    { icon: BookOpen, t: "Formation structurée" },
    { icon: ClipboardList, t: "Méthodes de travail claires" },
    { icon: HandHeart, t: "Accompagnement et rétroaction" },
    { icon: Wrench, t: "Possibilité de développer plusieurs compétences" },
    { icon: Trophy, t: "Progression selon la maîtrise et les responsabilités" },
    { icon: Compass, t: "Possibilité d'évoluer dans l'univers KGD" },
    { icon: Users, t: "Une entreprise à taille humaine" },
    { icon: Star, t: "Une culture basée sur la rigueur, la constance et la qualité" },
];

// Custom description shown ONLY on this recruitment page callout to
// keep the wording aligned with the brief. The main Collections pages
// remain untouched.
const COLLECTION_LABEL_OVERRIDES = {
    emeraude: "Entretien résidentiel régulier",
    rubis:    "Grand ménage / remise à niveau / après-construction",
    perle:    "Services à la carte",
    saphir:   "Entretien commercial",
    diamant:  "Résidentiel premium",
};

// ---------------------------------------------------------------
// Page
// ---------------------------------------------------------------
export default function DevenirCollectionneur() {
    useDocumentSEO(
        "Devenir Collectionneur KGD | Carrières — Ménage KGD",
        "Découvrez le parcours des Collectionneurs KGD, notre approche de formation, nos possibilités d'évolution et transmettez votre candidature à Ménage KGD."
    );
    useHighLevelEmbedScript();

    return (
        <div className="bg-[#F8F5EE]" data-testid="page-devenir-collectionneur">
            <HeroSection/>
            <JourneySection/>
            <AfterMasterySection/>
            <PassportSection/>
            <ProfileSection/>
            <OffersSection/>
            <FinalCTASection/>
            <RecruitmentFormSection/>
        </div>
    );
}

// ---------------------------------------------------------------
// Hero
// ---------------------------------------------------------------
function HeroSection() {
    return (
        <section
            className="relative overflow-hidden pt-14 pb-16 lg:pt-24 lg:pb-24"
            style={{ background: "linear-gradient(135deg,#020713 0%,#041126 55%,#07204A 130%)" }}
            data-theme="dark"
            data-testid="dc-hero"
        >
            <div aria-hidden="true" className="absolute -right-32 -top-32 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(232,212,139,0.16), transparent 70%)" }}/>
            <div aria-hidden="true" className="absolute -left-32 bottom-0 w-[560px] h-[560px] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(19,138,61,0.18), transparent 70%)" }}/>
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(232,212,139,0.5), transparent)" }}/>

            <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
                <div>
                    <div className="eyebrow" style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Carrières chez Ménage KGD</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mt-3 leading-[1.05]" data-testid="dc-hero-title">
                        Devenir<br/>
                        <span style={{ background: "linear-gradient(90deg,#75B5F0 0%,#E8D48B 50%,#4ADE80 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Collectionneur KGD</span>
                    </h1>
                    <p className="font-serif italic text-lg sm:text-xl mt-5" style={{ color: "#E8D48B" }}>
                        Plus qu'un emploi.<br className="hidden sm:block"/> Construisez votre parcours chez KGD.
                    </p>
                    <div className="mt-6 h-px w-24" style={{ background: "linear-gradient(90deg,#D4AF37,transparent)" }}/>
                    <p className="mt-6 text-base sm:text-lg max-w-xl leading-relaxed" style={{ color: "#E8EEF7", opacity: 0.95 }}>
                        Chez Ménage KGD, nous ne recherchons pas simplement des employés. Nous formons des Collectionneurs capables de représenter nos standards de rigueur, de constance et de qualité.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <a href="#candidature" className="btn-kgd" data-testid="dc-hero-cta-apply"><Sparkles className="w-4 h-4"/> Débuter ma candidature</a>
                        <a href="#parcours" className="btn-ghost-gold" data-testid="dc-hero-cta-discover">Découvrir le parcours</a>
                    </div>
                    <p className="mt-6 text-[11px] uppercase tracking-[0.28em] flex items-center gap-3" style={{ color: "#E8D48B" }} data-testid="dc-brand-signature">
                        Rigueur <span className="w-1 h-1 rounded-full" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                        Constance <span className="w-1 h-1 rounded-full" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                        Tranquillité d'esprit
                    </p>
                </div>
                {/* Cinematic visual — official KGD gems composition (Émeraude, Diamant, Rubis, Perle, Saphir) with luminous golden trajectories */}
                <div className="relative">
                    <figure className="relative rounded-2xl overflow-hidden border border-kgd-gold/30 shadow-[0_30px_70px_-24px_rgba(0,0,0,0.55)]" style={{ background: "linear-gradient(150deg,#050c1e 0%,#07204A 60%,#0A2555 130%)", aspectRatio: "4/5" }} data-testid="dc-hero-visual">
                        <img
                            src="/images/gems/parcours-hero.webp"
                            alt="Les cinq gemmes des Collections Ménage KGD reliées par une trajectoire dorée — Émeraude, Diamant, Rubis, Perle, Saphir"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="eager"
                            draggable="false"
                        />
                        {/* Subtle inner vignette to blend the image with the surrounding dark section */}
                        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 80% at 50% 30%, transparent 55%, rgba(2,7,19,0.35) 100%)" }}/>
                        {/* Warm gold hairline at the top */}
                        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(232,212,139,0.6), transparent)" }}/>
                        {/* Caption band */}
                        <figcaption className="absolute inset-x-0 bottom-0 p-5 sm:p-6" style={{ background: "linear-gradient(0deg, rgba(2,7,19,0.9) 0%, rgba(2,7,19,0.55) 55%, transparent 100%)" }}>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-kgd-gold">Parcours des Collectionneurs KGD</div>
                            <div className="font-serif text-white text-lg sm:text-xl mt-1">De la découverte à la maîtrise.</div>
                        </figcaption>
                    </figure>
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// Journey — 5 levels with realistic diamant progression
// ---------------------------------------------------------------
function JourneySection() {
    return (
        <section id="parcours" className="relative py-20 lg:py-28" style={{ background: "linear-gradient(180deg,#FBF8F1 0%,#F8F5EE 100%)" }} data-testid="dc-journey">
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg,transparent, rgba(212,175,55,0.5), transparent)" }}/>
            <div className="max-w-6xl mx-auto px-5 lg:px-8">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="eyebrow">Votre parcours chez KGD</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#041126] mt-3">Du potentiel à l'excellence.</h2>
                    <div className="mx-auto mt-5 h-px w-16" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                    <p className="mt-6 text-slate-700 leading-relaxed">
                        Chaque Collectionneur développe progressivement ses compétences, son autonomie et sa maîtrise des standards KGD.<br className="hidden sm:block"/>
                        La progression ne dépend pas uniquement de l'ancienneté. Elle repose sur la qualité du travail, la constance, la fiabilité, les compétences acquises et la capacité à représenter Ménage KGD.
                    </p>
                </div>

                <div className="mt-14 lg:mt-20">
                    {/* Desktop horizontal progression — same diamant, growing & more luminous */}
                    <div className="hidden lg:flex items-end justify-between max-w-5xl mx-auto mb-12 relative">
                        <div aria-hidden="true" className="absolute left-16 right-16 top-1/2 h-px -translate-y-2" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.6), transparent)" }}/>
                        {LEVELS.map((l) => (
                            <div key={l.n} className="relative flex flex-col items-center gap-3">
                                <RealGem gem="diamant" size={l.size} glow={l.glow} decorative/>
                                <span className="text-[10px] uppercase tracking-[0.24em] whitespace-nowrap" style={{ color: l.accent }}>Niveau 0{l.n}</span>
                            </div>
                        ))}
                    </div>

                    {/* Level cards — grid on desktop, stacked on mobile */}
                    <ol className="grid gap-5 sm:gap-6 lg:grid-cols-5">
                        {LEVELS.map((l) => (
                            <li key={l.n} className="luxe-card p-5 sm:p-6 relative overflow-hidden" data-testid={`dc-level-${l.n}`} style={{ borderColor: `${l.accent}30` }}>
                                <div className="flex items-center gap-3 lg:hidden mb-3">
                                    <RealGem gem="diamant" size={44} glow={l.glow} decorative/>
                                    <span className="text-[10px] uppercase tracking-[0.24em]" style={{ color: l.accent }}>Niveau 0{l.n}</span>
                                </div>
                                <div className="hidden lg:block text-[10px] uppercase tracking-[0.24em]" style={{ color: l.accent }}>Niveau 0{l.n}</div>
                                <h3 className="font-serif text-lg sm:text-xl text-[#041126] mt-1 leading-snug">{l.title}</h3>
                                <div className="mt-3 h-px w-10" style={{ background: `linear-gradient(90deg,${l.accent},transparent)` }}/>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{l.desc}</p>
                            </li>
                        ))}
                    </ol>

                    {/* Level 4 note — the 5 collections that can be mastered */}
                    <div className="mt-14 luxe-card p-6 sm:p-8 max-w-4xl mx-auto" data-testid="dc-collections-callout" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
                        <div className="text-center">
                            <div className="eyebrow">Rappel important</div>
                            <p className="mt-3 text-slate-700 leading-relaxed max-w-2xl mx-auto">
                                Devenir <strong className="text-[#041126]">Maître de sa Collection</strong> ne signifie pas maîtriser l'ensemble des Collections KGD. Chaque Collection possède ses propres apprentissages et son propre chemin de maîtrise.
                            </p>
                        </div>
                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
                            {COLLECTIONS.map(c => (
                                <div key={c.slug} className="rounded-xl p-4 text-center flex flex-col items-center gap-2" style={{ background: "#FFFFFF", border: `1px solid ${c.primary}30` }} data-testid={`dc-callout-gem-${c.slug}`}>
                                    <RealGem gem={c.slug} size={64} glow={0.65}/>
                                    <div className="font-serif text-sm sm:text-base text-[#041126] mt-1">{c.name}</div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-snug">{COLLECTION_LABEL_OVERRIDES[c.slug] || c.subtitle}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// After mastery — 4 branching paths
// ---------------------------------------------------------------
function AfterMasterySection() {
    return (
        <section className="relative py-20 lg:py-28 border-y border-kgd-gold/20 overflow-hidden" style={{ background: "linear-gradient(180deg,#F4EFE1 0%,#FBF8F1 100%)" }} data-testid="dc-after-mastery">
            <div className="max-w-6xl mx-auto px-5 lg:px-8">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="eyebrow">Et après ?</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#041126] mt-3 leading-tight">Votre évolution ne s'arrête pas là.</h2>
                    <div className="mx-auto mt-5 h-px w-16" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                    <p className="mt-6 text-slate-700 leading-relaxed">
                        Lorsqu'un Collectionneur atteint un haut niveau de maîtrise, plusieurs chemins peuvent s'ouvrir selon ses compétences, ses intérêts, son expérience et les besoins de Ménage KGD.
                    </p>
                </div>

                <div className="mt-12 lg:mt-16 grid gap-5 sm:gap-6 md:grid-cols-2">
                    {PATHS.map((p, i) => (
                        <div key={p.title} className="luxe-card p-6 sm:p-7 relative flex flex-col" data-testid={`dc-path-${i}`} style={{ borderColor: `${p.accent}30`, background: "#FFFFFF" }}>
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-[0_10px_25px_-10px_rgba(4,17,38,0.35)]" style={{ background: `linear-gradient(135deg,${p.accent}22,${p.accent}05)`, border: `1px solid ${p.accent}55` }}>
                                    <p.icon className="w-5 h-5" style={{ color: p.accent }}/>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-serif text-xl sm:text-2xl text-[#041126] leading-snug">{p.title}</h3>
                                    {p.note && <div className="mt-1 text-[11px] uppercase tracking-[0.22em]" style={{ color: p.accent }}>{p.note}</div>}
                                </div>
                            </div>
                            <div className="mt-4 h-px w-12" style={{ background: `linear-gradient(90deg,${p.accent},transparent)` }}/>
                            <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">{p.desc}</p>
                            {p.bullets && (
                                <ul className="mt-4 space-y-2">
                                    {p.bullets.map(b => (
                                        <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                                            <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: p.accent }}/>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {p.future && <p className="mt-4 text-xs italic text-slate-500">{p.future}</p>}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// Passeport KGD + Éclats — features the Académie KGD supporting visual
// ---------------------------------------------------------------
function PassportSection() {
    return (
        <section className="relative py-20 lg:py-28 overflow-hidden" style={{ background: "linear-gradient(180deg,#020713 0%,#041126 55%,#07204A 130%)" }} data-theme="dark" data-testid="dc-passport">
            <div aria-hidden="true" className="absolute -right-32 top-0 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(232,212,139,0.18), transparent 70%)" }}/>
            <div aria-hidden="true" className="absolute -left-24 bottom-0 w-[460px] h-[460px] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(19,138,61,0.18), transparent 70%)" }}/>

            <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                {/* Supporting visual — official Académie KGD asset */}
                <figure className="relative" data-testid="dc-academie-visual">
                    <div className="relative mx-auto max-w-md rounded-2xl overflow-hidden shadow-[0_35px_80px_-24px_rgba(0,0,0,0.7)]" style={{ border: "1px solid rgba(232,212,139,0.4)" }}>
                        <img
                            src="/images/gems/academie.webp"
                            alt="Académie KGD — Volumes d'apprentissage : Rigueur, Constance, Qualité, Formation, Progression"
                            className="w-full h-auto block"
                            loading="lazy"
                        />
                        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(2,7,19,0) 65%, rgba(2,7,19,0.25) 100%)" }}/>
                    </div>
                    <figcaption className="mt-4 text-center text-[11px] uppercase tracking-[0.28em] text-kgd-gold">
                        Académie KGD · Volumes d'apprentissage
                    </figcaption>
                </figure>

                <div>
                    <div className="eyebrow" style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Le Passeport KGD</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-tight">Votre progression devient visible.</h2>
                    <div className="mt-5 h-px w-16" style={{ background: "linear-gradient(90deg,#D4AF37,transparent)" }}/>
                    <p className="mt-6 leading-relaxed max-w-xl" style={{ color: "#E8EEF7", opacity: 0.95 }}>
                        Au fil de son parcours, un Collectionneur peut développer un dossier de progression regroupant ses apprentissages, certifications, Collections maîtrisées et responsabilités acquises.
                    </p>
                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
                        {["Niveaux atteints","Collections maîtrisées","Certifications","Formations complétées","Évaluations de qualité","Responsabilités acquises","Badges de Collection"].map(item => (
                            <li key={item} className="flex items-center gap-3 text-sm" style={{ color: "#E8EEF7" }}>
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}/>
                                {item}
                            </li>
                        ))}
                    </ul>

                    {/* Éclats KGD */}
                    <div className="mt-10 luxe-card p-5 sm:p-6 relative overflow-hidden" style={{ background: "linear-gradient(140deg, rgba(255,255,255,0.06), rgba(232,212,139,0.05))", border: "1px solid rgba(232,212,139,0.35)" }} data-testid="dc-eclats">
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37)" }}>
                                <Star className="w-4 h-4 text-[#041126]"/>
                            </div>
                            <div>
                                <div className="font-serif text-xl text-white">Éclats KGD</div>
                                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#E8EEF7", opacity: 0.9 }}>
                                    Les Éclats KGD pourront éventuellement reconnaître certaines réalisations, formations, constances ou contributions remarquables au parcours d'un Collectionneur.
                                </p>
                                <p className="mt-2 text-xs italic" style={{ color: "#CBD5E1", opacity: 0.8 }}>Concept en développement — aucune compensation monétaire n'est impliquée.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// Profile section
// ---------------------------------------------------------------
function ProfileSection() {
    return (
        <section className="relative py-20 lg:py-28" style={{ background: "linear-gradient(180deg,#FBF8F1 0%,#F4EFE1 100%)" }} data-testid="dc-profile">
            <div className="max-w-6xl mx-auto px-5 lg:px-8">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="eyebrow">Profil</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#041126] mt-3">Le profil d'un Collectionneur KGD</h2>
                    <div className="mx-auto mt-5 h-px w-16" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                </div>
                <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                    {QUALITIES.map(q => (
                        <div key={q.t} className="luxe-card p-5 flex flex-col items-center text-center gap-3">
                            <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37 55%,#B8912B)", boxShadow: "0 6px 18px -6px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.5)" }}>
                                <q.icon className="w-5 h-5 text-[#041126]" strokeWidth={1.75}/>
                            </div>
                            <div className="font-serif text-base sm:text-lg text-[#041126]">{q.t}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-12 max-w-3xl mx-auto text-center">
                    <div className="h-px w-16 mx-auto" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                    <p className="font-serif italic text-lg sm:text-xl text-[#041126] mt-5 leading-snug">
                        « La technique peut s'apprendre.<br className="hidden sm:block"/>
                        L'attitude, la fiabilité et la volonté de progresser font toute la différence. »
                    </p>
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// Offers
// ---------------------------------------------------------------
function OffersSection() {
    return (
        <section className="relative py-20 lg:py-28 border-t border-kgd-gold/20" style={{ background: "#F8F5EE" }} data-testid="dc-offers">
            <div className="max-w-6xl mx-auto px-5 lg:px-8">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="eyebrow">Ce que nous offrons</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#041126] mt-3">Un environnement de progression</h2>
                    <div className="mx-auto mt-5 h-px w-16" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                </div>
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {OFFERS.map(o => (
                        <div key={o.t} className="luxe-card p-5 flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37 55%,#B8912B)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)" }}>
                                <o.icon className="w-4.5 h-4.5 text-[#041126]" strokeWidth={1.75}/>
                            </div>
                            <div className="text-sm text-slate-700 leading-snug pt-1">{o.t}</div>
                        </div>
                    ))}
                </div>
                <p className="mt-10 text-center text-xs sm:text-sm italic text-slate-500 max-w-3xl mx-auto">
                    Les conditions, responsabilités et possibilités d'évolution peuvent varier selon le poste, l'expérience, les besoins de l'entreprise et les compétences démontrées.
                </p>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// Final CTA
// ---------------------------------------------------------------
function FinalCTASection() {
    return (
        <section className="relative py-20 lg:py-24 overflow-hidden" style={{ background: "linear-gradient(135deg,#020713 0%,#041126 45%,#07204A 100%)" }} data-theme="dark" data-testid="dc-final-cta-section">
            <div aria-hidden="true" className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(232,212,139,0.15), transparent 60%)" }}/>
            <div className="relative max-w-3xl mx-auto px-5 lg:px-8 text-center">
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">Prêt(e) à rejoindre la Collection ?</h2>
                <div className="mx-auto mt-6 h-px w-24" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                <p className="text-white/85 mt-6 leading-relaxed max-w-2xl mx-auto">
                    Si vous êtes fiable, rigoureux(se), professionnel(le) et fier(ère) du travail bien fait, nous aimerions découvrir votre profil.
                </p>
                <div className="mt-8 flex justify-center">
                    <a href="#candidature" className="btn-kgd" data-testid="dc-final-cta"><Sparkles className="w-4 h-4"/> Devenir Collectionneur KGD</a>
                </div>
                <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-kgd-gold/90">Rigueur ◆ Constance ◆ Tranquillité d'esprit</p>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------
// Recruitment form — DEDICATED HighLevel iframe (isolated from customers)
// ---------------------------------------------------------------
function RecruitmentFormSection() {
    return (
        <section id="candidature" className="relative py-16 lg:py-24 border-t border-kgd-gold/20" style={{ background: "linear-gradient(180deg,#FBF8F1 0%,#F4EFE1 100%)" }} data-testid="dc-form-section">
            <div className="max-w-3xl mx-auto px-5 lg:px-8">
                <div className="text-center">
                    <div className="eyebrow">Candidature</div>
                    <h2 className="font-serif text-3xl sm:text-4xl text-[#041126] mt-3" data-testid="dc-form-title">Transmettez votre profil</h2>
                    <div className="mx-auto mt-4 h-px w-16" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                    <p className="mt-5 text-sm text-slate-600 italic max-w-xl mx-auto">
                        Ce formulaire est exclusivement dédié aux candidatures et demeure indépendant de nos demandes clients.
                    </p>
                </div>

                <div className="mt-10 relative rounded-2xl overflow-hidden bg-white shadow-[0_30px_70px_-24px_rgba(4,17,38,0.25)]" style={{ border: "1px solid rgba(212,175,55,0.35)" }} data-testid="dc-form-card">
                    <div aria-hidden="true" className="h-[3px]" style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37,#B8912B)" }}/>
                    <div className="px-3 sm:px-5 lg:px-6 py-4 sm:py-6">
                        <div className="hl-form-shell w-full">
                            <iframe
                                src={HL_REC_SRC}
                                id={`inline-${HL_REC_FORM_ID}`}
                                className="hl-form-iframe"
                                style={{ width: "100%", border: "none", borderRadius: "8px", display: "block" }}
                                data-layout='{"id":"INLINE"}'
                                data-trigger-type="alwaysShow"
                                data-trigger-value=""
                                data-activation-type="alwaysActivated"
                                data-activation-value=""
                                data-deactivation-type="neverDeactivate"
                                data-deactivation-value=""
                                data-form-name="Devenir Collectionneur KGD - Recrutement"
                                data-height="undefined"
                                data-layout-iframe-id={`inline-${HL_REC_FORM_ID}`}
                                data-form-id={HL_REC_FORM_ID}
                                data-cookie-consent="true"
                                data-cookie-consent-provider="auto"
                                title="Devenir Collectionneur KGD — formulaire de candidature"
                                data-testid="highlevel-recruitment-iframe"
                                loading="eager"
                                referrerPolicy="no-referrer-when-downgrade"
                                allow="clipboard-write; camera; microphone"
                            />
                        </div>
                    </div>
                </div>

                <p className="text-center text-xs text-slate-500 mt-6 italic px-4">
                    Ceci est la première étape du processus de recrutement Ménage KGD. Vos informations sont utilisées uniquement pour évaluer votre candidature.
                </p>
            </div>
        </section>
    );
}
